<template>
  <div class="hearing-test">
    <HeroBanner
        :background-image="bannerImage"
        title="청력 검사"
        sub-title="다비치 히어링 보청기만의 차별화된 청력 분석 프로그램인 이카스(ECAS)를 통해 고객님의 난청 상태, 유형, 생활 습관 등을
종합적으로 진단합니다."
    />

    <!-- 검사 항목들 -->
    <div class="test-sections">
      <!-- 문진 -->
      <section class="test-item">
        <div class="test-item__content">
          <h3 class="test-item__title">문진</h3>
          <p class="test-item__description">
            환자의 증상, 청력 저하 시기, 소음 노출 여부, 이명·어지럼증 동반 여부 등 청력 관련 병력과 생활 습관을 확인하는 과정입니다.<br>
            환자의 주관적 청각 불편을 파악하고 이후 검사 방향을 설정하기 위한 1차 평가입니다.
          </p>
        </div>
        <div class="test-item__image">
          <img
            :src="require('@/assets/images/banners/02/02-04-img-01.png')"
            :srcset="`${require('@/assets/images/banners/02/02-04-img-01.png')} 1x, ${require('@/assets/images/banners/02/02-04-img-01@2x.png')} 2x`"
            alt="문진"
          >
        </div>
      </section>

      <!-- 이경 검사 -->
      <section class="test-item">
        <div class="test-item__content">
          <h3 class="test-item__title">이경 검사</h3>
          <p class="test-item__description">
            이경(otoscope)을 사용하여 외이도 및 고막 상태를 시각적으로 확인합니다. <br>
            귀지(이물질), 외이도염, 고막 천공, 삼출액 등 기도 전달을 방해할 수 있는 요인을 판별합니다.
          </p>
        </div>
        <div class="test-item__image">
          <img
            :src="require('@/assets/images/banners/02/02-04-img-02.png')"
            :srcset="`${require('@/assets/images/banners/02/02-04-img-02.png')} 1x, ${require('@/assets/images/banners/02/02-04-img-02@2x.png')} 2x`"
            alt="이경 검사"
          >
        </div>
      </section>

      <!-- 순음(기도) 검사 -->
      <section class="test-item">
        <div class="test-item__content">
          <h3 class="test-item__title">순음(기도) 검사</h3>
          <p class="test-item__description">
            헤드폰을 통해 다양한 주파수(보통 250~8000Hz)의 순음을 제시하여 청력 역치를 측정합니다.<br>
            공기를 매개로 소리를 들려줌으로써 전체 청각 경로(외이, 중이, 내이, 청신경)의 기능을 종합적으로 평가합니다.
          </p>
        </div>
        <div class="test-item__image">
          <img
            :src="require('@/assets/images/banners/02/02-04-img-03.png')"
            :srcset="`${require('@/assets/images/banners/02/02-04-img-03.png')} 1x, ${require('@/assets/images/banners/02/02-04-img-03@2x.png')} 2x`"
            alt="순음(기도) 검사"
          >
        </div>
      </section>

      <!-- 어음(WRS) 검사 -->
      <section class="test-item">
        <div class="test-item__content">
          <h3 class="test-item__title">어음(WRS) 검사</h3>
          <p class="test-item__description">
            단어 혹은 문장을 들려주어 인식률과 이해도를 평가하며, 난청이 일상 대화 이해에 미치는 영향을 측정합니다. <br>
            어음인지역치(SRT), 어음인지도(WRS) 등을 산출하여 청취 상황에서의 실제 의사소통 능력을 평가합니다.
          </p>
        </div>
        <div class="test-item__image">
          <img
            :src="require('@/assets/images/banners/02/02-04-img-04.png')"
            :srcset="`${require('@/assets/images/banners/02/02-04-img-04.png')} 1x, ${require('@/assets/images/banners/02/02-04-img-04@2x.png')} 2x`"
            alt="어음(WRS) 검사"
          >
        </div>
      </section>

      <!-- 순음(골도) 검사 -->
      <section class="test-item">
        <div class="test-item__content">
          <h3 class="test-item__title">순음(골도) 검사</h3>
          <p class="test-item__description">
            진동기를 귀 뒤 뼈(유양돌기)에 부착하여 내이와 청신경을 직접 자극해 청력 역치를 측정합니다. <br>
            기도 검사와 비교하여 전도성 난청(외이/중이 문제)과 감각신경성 난청(내이/청신경 문제)을 구분합니다.
          </p>
        </div>
        <div class="test-item__image">
          <img
            :src="require('@/assets/images/banners/02/02-04-img-05.png')"
            :srcset="`${require('@/assets/images/banners/02/02-04-img-05.png')} 1x, ${require('@/assets/images/banners/02/02-04-img-05@2x.png')} 2x`"
            alt="순음(골도) 검사"
          >
        </div>
      </section>

      <!-- 실이측정(REM) -->
      <section class="test-item">
        <div class="test-item__content">
          <h3 class="test-item__title">실이측정(REM)</h3>
          <p class="test-item__description">
            얇은 프로브 마이크를 귀 안쪽에 삽입하고 보청기를 착용한 상태에서 실제 증폭된 음압을 측정합니다. <br>
            보청기의 음향 출력이 개인의 외이도 특성과 청력 손실 정도에 맞게 조정되었는지 객관적으로 확인합니다.
          </p>
        </div>
        <div class="test-item__image">
          <img
            :src="require('@/assets/images/banners/02/02-04-img-06.png')"
            :srcset="`${require('@/assets/images/banners/02/02-04-img-06.png')} 1x, ${require('@/assets/images/banners/02/02-04-img-06@2x.png')} 2x`"
            alt="실이측정(REM)"
          >
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import HeroBanner from "@/components/HeroBanner.vue";
import bannerBg from '@/assets/images/heros/02-top-img.png';
import bannerBg2 from '@/assets/images/heros/02-top-img@2x.png';

export default {
  name: 'HearingAidTest',
  components: {HeroBanner},
  data() {
    return {
      bannerImage: [bannerBg, bannerBg2],
    };
  }
};
</script>

<style scoped lang="scss">
.hearing-test {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  background: #fff;
}

.test-sections {
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  gap: 50px;
}

.test-item {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__content {
    padding: 0 36px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__title {
    font-family: 'Noto Sans KR', sans-serif;
    font-style: normal;
    font-weight: 700;
    font-size: 26px;
    line-height: 38px;
    letter-spacing: -0.02em;
    color: #222222;
    margin: 0;
  }

  &__description {
    font-family: 'Noto Sans KR', sans-serif;
    font-style: normal;
    font-weight: 350;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: -0.04em;
    color: #666666;
    margin: 0;
    white-space: pre-line;
  }

  &__image {
    width: 100%;
    overflow: hidden;

    img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }
  }
}

@media (max-width: 768px) {
  .test-sections {
    padding: 30px 0;
    gap: 40px;
  }

  .test-item {
    gap: 16px;

    &__content {
      padding: 0 20px;
    }

    &__title {
      font-size: 22px;
      line-height: 32px;
    }

    &__description {
      font-size: 15px;
      line-height: 26px;
    }
  }
}
</style>
